/**
 * Utilidades para gestionar el almacenamiento local en ExceliaOne
 */

/**
 * Obtiene la estimación de almacenamiento utilizado y disponible
 * @returns {Promise<Object>} Información de almacenamiento
 */
export const getStorageEstimate = async () => {
  if (!navigator.storage || !navigator.storage.estimate) {
    throw new Error("Tu navegador no soporta la API de Storage Manager");
  }

  try {
    const estimate = await navigator.storage.estimate();

    return {
      raw: estimate,
      usage: estimate.usage || 0,
      quota: estimate.quota || 0,
      percent: estimate.quota ? Math.round((estimate.usage / estimate.quota) * 100) : 0,
      usageMB: Math.round((estimate.usage || 0) / (1024 * 1024)),
      quotaMB: Math.round((estimate.quota || 0) / (1024 * 1024)),
      available: (estimate.quota || 0) - (estimate.usage || 0),
      availableMB: Math.round(((estimate.quota || 0) - (estimate.usage || 0)) / (1024 * 1024)),
      usageGB: ((estimate.usage || 0) / (1024 * 1024 * 1024)).toFixed(2),
      quotaGB: ((estimate.quota || 0) / (1024 * 1024 * 1024)).toFixed(2),
      availableGB: (((estimate.quota || 0) - (estimate.usage || 0)) / (1024 * 1024 * 1024)).toFixed(
        2
      ),
    };
  } catch (error) {
    console.error("Error obteniendo estimación de almacenamiento:", error);
    throw error;
  }
};

/**
 * Solicita almacenamiento persistente
 * @returns {Promise<Boolean>} True si el almacenamiento es persistente
 */
export const requestPersistentStorage = async () => {
  if (!navigator.storage || !navigator.storage.persist) {
    throw new Error("Tu navegador no soporta almacenamiento persistente");
  }

  try {
    // Comprobar primero si ya es persistente
    const isPersisted = await navigator.storage.persisted();
    if (isPersisted) {
      return true;
    }

    // Solicitar persistencia
    return await navigator.storage.persist();
  } catch (error) {
    console.error("Error solicitando almacenamiento persistente:", error);
    throw error;
  }
};

/**
 * Comprueba si el almacenamiento es persistente
 * @returns {Promise<Boolean>} True si el almacenamiento es persistente
 */
export const isStoragePersistent = async () => {
  if (!navigator.storage || !navigator.storage.persisted) {
    return false;
  }

  try {
    return await navigator.storage.persisted();
  } catch (error) {
    console.error("Error comprobando si el almacenamiento es persistente:", error);
    return false;
  }
};

/**
 * Formatea bytes a una unidad legible para humanos
 * @param {Number} bytes - Cantidad de bytes
 * @param {Number} decimals - Número de decimales
 * @returns {String} Tamaño formateado con unidad
 */
export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + " " + sizes[i];
};
